"use client"

import { HttpTypes } from "@medusajs/types"
import { Text, Button } from "@medusajs/ui"
import type { ViewMode } from "../../templates/search-template"
import type { SearchResponse } from "@/lib/data/search"
import ProductGrid from "../product-grid"
import SearchPagination from "../search-pagination"

type SearchResultsProps = {
  results: SearchResponse | null
  loading: boolean
  error: string | null
  viewMode: ViewMode
  currentPage: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  countryCode: string
  customer?: HttpTypes.StoreCustomer | null
  /**
   * Only the global /search PLP should set this. Category/collection PLPs must omit it so
   * `search_results_clicked` is not inflated by generic product grid clicks.
   */
  searchResultsClickSurface?: "search_grid" | "search_list"
}

export function SearchErrorState({ error }: { error: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-navy p-8 text-center">
      <div className="max-w-md mx-auto">
        <Text className="text-lg font-semibold text-gold mb-2">
          Napaka pri iskanju
        </Text>
        <Text className="text-navy mb-4">
          {error}
        </Text>
        <Button onClick={() => window.location.reload()}>
          Poskusi znova
        </Button>
      </div>
    </div>
  )
}

export function SearchLoadingState({
  viewMode,
  itemsPerPage,
}: {
  viewMode: ViewMode
  itemsPerPage: number
}) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-navy rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="animate-spin w-5 h-5 border-2 border-gold border-t-transparent rounded-full"></div>
          <span className="text-gold font-medium">Iskanje izdelkov...</span>
        </div>
      </div>
      {viewMode === "grid" ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: itemsPerPage }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-navy p-4 animate-pulse">
              <div className="aspect-square bg-gray-200 rounded-lg mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-6 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from({ length: itemsPerPage }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-navy p-6 animate-pulse">
              <div className="flex gap-6">
                <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function SearchEmptyState() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-navy p-12 text-center">
      <div className="max-w-md mx-auto">
        <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <Text className="text-4xl">🔍</Text>
        </div>
        <Text className="text-xl font-semibold text-navy mb-2">
          Ni najdenih izdelkov
        </Text>
        <Text className="text-navy mb-6">
          Poskusite prilagoditi iskalne izraze ali filtre, da najdete to, kar iščete.
        </Text>
        <div className="space-y-2">
          <Text className="text-sm font-medium text-navy">
            Predlogi:
          </Text>
          <ul className="text-sm text-navy space-y-1">
            <li>• Preverite črkovanje</li>
            <li>• Uporabite bolj splošne izraze</li>
            <li>• Odstranite nekatere filtre</li>
            <li>• Poskusite različne kategorije</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export function SearchUpdatingNotice() {
  return (
    <div className="bg-white border border-navy rounded-lg p-3">
      <div className="flex items-center gap-3">
        <div className="animate-spin w-4 h-4 border-2 border-gold border-t-transparent rounded-full"></div>
        <span className="text-gold text-sm">Posodabljanje rezultatov...</span>
      </div>
    </div>
  )
}

export default function SearchResults({
  results,
  loading,
  error,
  viewMode,
  currentPage,
  itemsPerPage,
  onPageChange,
  countryCode,
  customer,
  searchResultsClickSurface,
}: SearchResultsProps) {
  const totalPages = results ? Math.ceil(results.estimatedTotalHits / itemsPerPage) : 0

  if (error) {
    return <SearchErrorState error={error} />
  }

  // Show skeleton only if no results yet
  if (loading && !results) {
    return <SearchLoadingState viewMode={viewMode} itemsPerPage={itemsPerPage} />
  }

  if (!results || results.hits.length === 0) {
    return <SearchEmptyState />
  }

  return (
    <div className="space-y-6">
      {/* Loading indicator when updating results */}
      {loading && <SearchUpdatingNotice />}

      <div className={loading ? "opacity-75 transition-opacity duration-200" : ""}>
        <ProductGrid
          hits={results.hits}
          viewMode={viewMode}
          countryCode={countryCode}
          customer={customer}
          searchResultsClickSurface={searchResultsClickSurface}
          priorityCount={4}
        />
      </div>

      {totalPages > 1 && (
        <SearchPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalHits={results.estimatedTotalHits}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
